import { isNativeShellApp, normalizeClientId, readOrCreateClientId } from './clientId';
import { persistClientIdBackups } from './clientIdBackup';
import { getNativeClientId, NATIVE_CLIENT_ID_TIMEOUT_MS, setNativeClientId } from './clientIdNative';

function writeClientIdLocal(value) {
  localStorage.setItem('clientId', value);
}

function applyNativeClientId(nativeId) {
  writeClientIdLocal(nativeId);
  persistClientIdBackups(nativeId);
  return nativeId;
}

/**
 * Resolve clientId before React mounts.
 * On native shell: native storage wins over WebView localStorage.
 * On web: same as readOrCreateClientId().
 *
 * @returns {Promise<string>} resolved clientId
 */
export async function bootstrapClientId(timeoutMs = NATIVE_CLIENT_ID_TIMEOUT_MS) {
  if (!isNativeShellApp()) {
    return readOrCreateClientId();
  }

  const nativeGet = getNativeClientId(timeoutMs);
  const timedOutValue = await nativeGet;
  const fromNative = normalizeClientId(timedOutValue);

  if (fromNative) {
    return applyNativeClientId(fromNative);
  }

  // Timed out or empty — use WebView storage / generate, then seed native.
  const webId = readOrCreateClientId();
  setNativeClientId(webId);

  // Late native response after timeout: if it differs, restore and reload once.
  const late = nativeGet.readPromise;
  if (late && typeof late.then === 'function') {
    late
      .then(raw => {
        const restored = normalizeClientId(raw);
        if (!restored || restored === webId) {
          return;
        }
        applyNativeClientId(restored);
        if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
          window.location.reload();
        }
      })
      .catch(() => {
        // ignore
      });
  }

  return webId;
}
